import app from 'flarum/forum/app';
import FormModal from 'flarum/common/components/FormModal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import Icon from 'flarum/common/components/Icon';
import { checkPassword } from '../../common/accountRules';
import { withEye } from './PasswordEye';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.account.' + key, params);
export const REASONS = ['old_unused', 'no_access', 'hacked', 'typo', 'other'];
export const NOTE_MAX = 200;

/** A password field must stay a password field, whatever touched it in between. */
export function enforceType(el, type) {
  // v17: «Показать пароль» (PasswordEye) — a shown password stays text until hidden again
  const want = type === 'password' && el && el.__ppk2Reveal ? 'text' : type;
  if (el && (el.type !== want || el.getAttribute('type') !== want)) el.setAttribute('type', want);
}

/** enforceType at once and whenever something (a script, an add-on) changes the type attribute. */
export function guardType(el, type) {
  enforceType(el, type);
  if (!el || el.__ppk2Guard || typeof MutationObserver === 'undefined') return;
  el.__ppk2Guard = new MutationObserver(() => enforceType(el, type));
  el.__ppk2Guard.observe(el, {
    attributes: true,
    attributeFilter: ['type']
  });
}

/**
 * Shared: POST /api/ppk2/account/<type>; 422 messages are shown under their fields.
 * Both changes are immediate (not part of «Сохранить» / «Отменить все изменения»).
 */
class AccountModal extends FormModal {
  oninit(vnode) {
    super.oninit(vnode);
    this.errors = {};
  }
  className() {
    return 'Modal--small Ppk2AccountModal';
  }

  /**
   * One field. extra: hint / live status under the field (password rule).
   *
   * v16: the inputs are NOT bound to `value` (Mithril would write the stream back into the
   * input on every redraw and wipe a value the browser / password manager filled in without an
   * input event — that is how «Текущий пароль» became empty). The stream follows the input
   * (input + change events) and the DOM is read again on submit. The type is fixed per field
   * and re-applied on every redraw and focus, so a password field can never stay as plain text.
   * Every group and input has a key, so Mithril never moves a DOM input to another field.
   */
  input(name, label, stream, attrs = {}, extra = null) {
    const bad = this.errors[name] || extra && extra.bad;
    const type = attrs.type || 'text';
    const sync = e => {
      enforceType(e.target, type);
      if (stream() !== e.target.value) {
        stream(e.target.value);
        delete this.errors[name];
      } else e.redraw = false;
    };
    const input = m("input", Object.assign({}, attrs, {
      id: 'ppk2-' + name,
      className: 'FormControl' + (extra && extra.bad ? ' Ppk2AccountModal-bad' : extra && extra.ok ? ' Ppk2AccountModal-ok' : ''),
      name: name,
      type: type,
      spellcheck: "false",
      oncreate: vn => {
        vn.dom.value = stream();
        guardType(vn.dom, type);
      },
      onremove: vn => vn.dom.__ppk2Guard && vn.dom.__ppk2Guard.disconnect(),
      onupdate: vn => enforceType(vn.dom, type),
      onfocus: e => {
        enforceType(e.target, type);
        e.redraw = false;
      },
      oninput: sync,
      onchange: sync,
      disabled: this.loading
    }));
    return m("div", {
      key: 'group-' + name,
      className: 'Form-group Ppk2AccountModal-group--' + name + (bad ? ' Ppk2AccountModal-hasError' : '')
    }, m("label", {
      for: 'ppk2-' + name
    }, label), type === 'password' ? withEye(input) : input, extra && extra.hint ? m("div", {
      className: "Ppk2AccountModal-hint"
    }, extra.hint) : null, this.errors[name] ? m("div", {
      className: "Ppk2AccountModal-error"
    }, this.errors[name]) : extra && extra.message ? m("div", {
      className: "Ppk2AccountModal-error"
    }, m(Icon, {
      name: "fas fa-circle-xmark"
    }), " ", extra.message) : null);
  }

  /** Values the browser filled in without any event: take them from the DOM before sending. */
  readDom(map) {
    Object.keys(map).forEach(name => {
      const el = this.element && this.element.querySelector('[name="' + name + '"]');
      if (el && el.value !== map[name]()) map[name](el.value);
    });
  }
  send(type, body) {
    this.loading = true;
    this.errors = {};
    this.alertAttrs = null;
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ppk2/account/' + type,
      body,
      errorHandler: error => {
        const list = error.response && error.response.errors || [];
        list.forEach(e => {
          const field = (e.source && e.source.pointer || '').split('/').pop();
          if (field) this.errors[field] = e.detail;
        });
        if (!list.length) app.alerts.show({
          type: 'error'
        }, error.message || 'Error');
        throw error;
      }
    }).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }
}

/** «Пароль» → «Изменить»: current + new + repeat, no reason. */
export class PasswordModal extends AccountModal {
  oninit(vnode) {
    super.oninit(vnode);
    this.current = Stream('');
    this.password = Stream('');
    this.repeat = Stream('');
  }
  title() {
    return t('pw_title');
  }

  /** «Новый пароль»: the rule as a hint, the first problem in red while typing. */
  passwordHint() {
    const err = this.password() ? checkPassword(this.password()) : null;
    return {
      hint: app.translator.trans('ppk2-profile.lib.rules.password.hint'),
      bad: !!err,
      ok: this.password() && !err,
      message: err ? app.translator.trans(err[0], err[1]) : null
    };
  }
  repeatHint() {
    const mismatch = this.repeat() && this.repeat() !== this.password();
    return {
      bad: mismatch,
      ok: this.repeat() && !mismatch,
      message: mismatch ? app.translator.trans('ppk2-profile.lib.account.password_mismatch') : null
    };
  }
  content() {
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form"
    }, this.input('currentPassword', t('pw_current'), this.current, {
      type: 'password',
      autocomplete: 'current-password'
    }), this.input('newPassword', t('pw_new'), this.password, {
      type: 'password',
      autocomplete: 'new-password'
    }, this.passwordHint()), this.input('newPasswordRepeat', t('pw_repeat'), this.repeat, {
      type: 'password',
      autocomplete: 'new-password'
    }, this.repeatHint()), m("p", {
      key: "help",
      className: "helpText"
    }, t('pw_help')), m("div", {
      key: "submit",
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.loading
    }, t('pw_submit')))));
  }
  onsubmit(e) {
    e.preventDefault();
    this.readDom({
      currentPassword: this.current,
      newPassword: this.password,
      newPasswordRepeat: this.repeat
    });
    const err = checkPassword(this.password());
    if (err || this.repeat() !== this.password()) {
      if (err) this.errors.newPassword = app.translator.trans(err[0], err[1]);else this.errors.newPasswordRepeat = app.translator.trans('ppk2-profile.lib.account.password_mismatch');
      return;
    }
    this.send('password', {
      currentPassword: this.current(),
      newPassword: this.password(),
      newPasswordRepeat: this.repeat()
    }).then(() => {
      this.hide();
      app.alerts.show({
        type: 'success'
      }, t('pw_done'));
    }, () => {});
  }
}

/** «Почта» → «Изменить»: step 1 reason (+ text for «Другое»), step 2 new email + password. */
export class EmailModal extends AccountModal {
  oninit(vnode) {
    super.oninit(vnode);
    this.step = 1;
    this.reason = Stream('');
    this.note = Stream('');
    this.email = Stream('');
    this.password = Stream('');
  }
  title() {
    return t('em_title');
  }
  stepOneValid() {
    return this.reason() && (this.reason() !== 'other' || this.note().trim().length > 0) && this.note().length <= NOTE_MAX;
  }
  content() {
    if (this.step === 1) {
      return m("div", {
        className: "Modal-body"
      }, m("div", {
        className: "Form"
      }, m("div", {
        className: "Form-group"
      }, m("label", null, t('em_reason')), m("div", {
        className: "Ppk2AccountModal-reasons",
        role: "radiogroup"
      }, REASONS.map(r => m("label", {
        className: "checkbox"
      }, m("input", {
        type: "radio",
        name: "reason",
        value: r,
        checked: this.reason() === r,
        onchange: () => this.reason(r)
      }), t('r_' + r)))), this.errors.reason ? m("div", {
        className: "Ppk2AccountModal-error"
      }, this.errors.reason) : null), this.reason() === 'other' ? m("div", {
        className: 'Form-group' + (this.errors.note ? ' Ppk2AccountModal-hasError' : '')
      }, m("textarea", {
        className: "FormControl",
        name: "note",
        rows: "3",
        maxlength: NOTE_MAX,
        placeholder: t('em_note'),
        bidi: this.note
      }), m("div", {
        className: "helpText Ppk2AccountModal-count"
      }, this.note().length, "/", NOTE_MAX), this.errors.note ? m("div", {
        className: "Ppk2AccountModal-error"
      }, this.errors.note) : null) : null, m("div", {
        className: "Form-group"
      }, m(Button, {
        className: "Button Button--primary Button--block",
        type: "submit",
        disabled: !this.stepOneValid()
      }, t('em_next')))));
    }
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form"
    }, m("p", {
      key: "reason",
      className: "helpText"
    }, t('em_reason'), " ", m("b", null, t('r_' + this.reason()))), this.input('email', t('em_new'), this.email, {
      type: 'email',
      autocomplete: 'email',
      maxlength: 150
    }), this.input('password', t('em_password'), this.password, {
      type: 'password',
      autocomplete: 'current-password'
    }), m("p", {
      key: "help",
      className: "helpText"
    }, t('em_help')), m("div", {
      key: "buttons",
      className: "Form-group Ppk2AccountModal-buttons"
    }, m(Button, {
      className: "Button",
      type: "button",
      disabled: this.loading,
      onclick: () => (this.step = 1, this.errors = {})
    }, t('em_back')), m(Button, {
      className: "Button Button--primary",
      type: "submit",
      loading: this.loading
    }, t('em_submit')))));
  }
  onsubmit(e) {
    e.preventDefault();
    if (this.step === 1) {
      if (this.stepOneValid()) {
        this.step = 2;
        this.errors = {};
      }
      return;
    }
    this.readDom({
      email: this.email,
      password: this.password
    });
    const body = {
      reason: this.reason(),
      note: this.reason() === 'other' ? this.note() : '',
      email: this.email(),
      password: this.password()
    };
    this.send('email', body).then(r => {
      const user = app.session.user;
      if (user && r && r.result && r.result.email) user.pushAttributes({
        email: r.result.email
      });
      this.hide();
      app.alerts.show({
        type: 'success'
      }, t('em_done'));
    }, () => {
      if (this.errors.reason || this.errors.note) this.step = 1;
    });
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/AccountModals', { REASONS: REASONS,NOTE_MAX: NOTE_MAX,enforceType: enforceType,guardType: guardType,PasswordModal: PasswordModal,EmailModal: EmailModal, });